import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { FetchPolicy, gql } from '@apollo/client/core';
import { AppConfig } from '@app/core/app.config';
import { Investment, InvestmentInput } from '@app/core/model/entities/investment/investment';
import { ActivationEndService } from '@app/features/main/activation-end.service';
import { SelectParams } from '@app/shared/grid/datasource-utils';
import ApiService from '@services/api.service';
import { AppManager } from '@services/managers/app.manager';
import { plainToInstance } from 'class-transformer';
import { EMPTY, expand, from, Observable, Subject } from 'rxjs';
import { map, reduce, switchMap, tap, toArray } from 'rxjs/operators';

@Injectable({providedIn: 'root'})
export class InvestmentsService {

  private sidePanelToggle = new Subject<Investment | null>();
  private investmentAdded = new Subject<Investment>();
  private investmentUpdated = new Subject<Investment>();
  private investmentsDeleted = new Subject<Investment[]>();

  private readonly investmentInfoGraphqlFragment = gql`
    fragment InvestmentInfo on Investment {
      id
      name
      identifier
      type
      entityId
      organizationId
      properties
      computedProperties
      creationActor {
        actorId
        name
      }
      creationDate
      lastChangeActor {
        actorId
        name
      }
      lastChangeDate
      dataDate
    }
  `;

  private readonly appManager = inject(AppManager);
  private readonly activationEndService = inject(ActivationEndService);
  private readonly apiService = inject(ApiService);
  private readonly router = inject(Router);
  private readonly appConfig = inject(AppConfig);

  /**
   * Emits data for the side panel to display or null whenever the side panel is closed.
   */
  public get sidePanelToggle$(): Observable<Investment | null> {
    return this.sidePanelToggle.asObservable();
  }

  /**
   * Emits the new Investment after it was created.
   * @returns Observable emitting the new Investment.
   */
  public get investmentAdded$(): Observable<Investment> {
    return this.investmentAdded.asObservable();
  }

  /**
   * Emits the updated Investment after it was updated.
   * @returns Observable emitting the updated Investment.
   */
  public get investmentUpdated$(): Observable<Investment> {
    return this.investmentUpdated.asObservable();
  }

  /**
   * Emits the deleted Investment after it was deleted.
   * @returns Observable emitting the deleted Investment.
   */
  public get investmentsDeleted$(): Observable<Investment[]> {
    return this.investmentsDeleted.asObservable();
  }

  /**
   * Make an API request to fetch an Investment.
   * @param id ID of the Investment.
   * @param fetchPolicy GraphQL query fetch policy, default is 'network-only'.
   * @return Observable emitting a list of all accessible Investments.
   */
  public loadInvestment(id: string, fetchPolicy: FetchPolicy = 'network-only'): Observable<Investment> {
    const query = gql`
      query Investment($id: String!) {
        investment(id: $id) {
          ...InvestmentInfo
        }
      } ${this.investmentInfoGraphqlFragment}
    `;
    const variables = {id};

    return this.apiService.query({query, variables, fetchPolicy})
      .pipe(
        map(data => plainToInstance(Investment, data['investment'])),
        tap(() => {
          if (fetchPolicy === 'cache-first') {
            this.activationEndService.getRefreshHeaderSubject().next();
          }
        })
      );
  }

  /**
   * Loads an investment page based on the provided select parameters.
   *
   * This function performs a GraphQL query to fetch investments based on the provided select parameters.
   * The select parameters include sorting, filtering, grouping, pagination.
   * It also fetches information about users who created and last updated the investments.
   *
   * @param selectParams The parameters for the select query. These include sorting, filtering, grouping, pagination, and locale settings.
   * @return An Observable emitting an array of Investment objects, each with additional user information.
   */
  public loadInvestmentsPage(selectParams: SelectParams): Observable<Investment[]> {
    const query = gql`
      query Investments($organizationId: String!, $selectParams: SelectParams!) {
        investments(organizationId: $organizationId, selectParams: $selectParams) {
          ...InvestmentInfo
        }
      }
      ${this.investmentInfoGraphqlFragment}
    `;
    const variables = {
      organizationId: this.appManager.currentOrganization.id,
      selectParams: selectParams
    };

    return this.apiService.query({query, variables})
      .pipe(map(data => plainToInstance(Investment, data['investments'] as Investment[])));
  }

  /**
   * Loads all investments pages based on the provided select parameters.
   *
   * This function performs a GraphQL query to fetch investments based on the provided select parameters.
   * The select parameters include sorting, filtering, grouping.
   * It also fetches information about users who created and last updated the investments.
   *
   * @param selectParams The parameters for the select query. These include sorting, filtering, grouping, and locale settings.
   * @return An Observable emitting an array of Investment objects, each with additional user information.
   */
  public loadAllInvestments(selectParams: SelectParams): Observable<Investment[]> {
    const pageSize = this.appConfig.MAX_REQUEST_PAGE_SIZE;
    const params = {...selectParams, start: 0, count: pageSize};
    return this.loadInvestmentsPage(params)
      .pipe(
        expand((investments, i) => {
          if (investments.length < pageSize) {
            return EMPTY;
          }
          params.start = (i + 1) * pageSize;
          return this.loadInvestmentsPage(params);
        }),
        reduce((all, page) => {
          all.push(...page);
          return all;
        }, [] as Investment[])
      );
  }

  /**
   * Create a new Investment with the provided data.
   * @param investmentInput Investment data.
   * @returns An Observable emitting the new Investment that was created.
   */
  public createInvestment(investmentInput: InvestmentInput): Observable<Investment> {
    const mutation = gql`
      mutation CreateInvestment($investmentInput: CreateInvestmentInput!) {
        createInvestment(investmentInput: $investmentInput) {
          ...InvestmentInfo
        }
      }
      ${this.investmentInfoGraphqlFragment}
    `;
    const variables = {
      investmentInput: {
        organizationId: this.appManager.currentOrganization.id,
        ...investmentInput
      }
    };

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(Investment, data['createInvestment'])),
        tap(investment => this.investmentAdded.next(investment))
      );
  }

  /**
   * Call the API to update an Investment.
   * @param investment Investment to update.
   * @param investmentInput Data to update the Investment with.
   * @returns An Observable emitting the updated Investment once the update is completed.
   */
  public updateInvestment(investment: Investment, investmentInput: InvestmentInput): Observable<Investment> {
    const mutation = gql`
      mutation UpdateInvestment($investmentInput: UpdateInvestmentInput!) {
        updateInvestment(investmentInput: $investmentInput) {
          ...InvestmentInfo
        }
      }
      ${this.investmentInfoGraphqlFragment}
    `;
    const variables = {
      investmentInput: {
        id: investment.id,
        name: investment.name,
        entityId: investment.entityId,
        type: investment.type,
        ...investmentInput
      }
    };

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(Investment, data['updateInvestment'])),
        tap(investment => this.investmentUpdated.next(investment))
      );
  }

  /**
   * Call the API to update a list of Investments.
   * @param investments A list of Investment to update.
   * @returns An Observable emitting updated Investments once the update is completed.
   */
  public updateInvestments(
    investments: { investment: Investment, investmentInput: InvestmentInput }[]
  ): Observable<Investment[]> {
    const mutation = gql`
      mutation UpdateInvestments($investmentInputs: [UpdateInvestmentInput!]!) {
        updateInvestments(investmentInputs: $investmentInputs) {
          ...InvestmentInfo
        }
      }
      ${this.investmentInfoGraphqlFragment}
    `;
    const variables = {
      investmentInputs: investments.map(({investment: {id, name, entityId, type}, investmentInput}) => {
        return {id, name, entityId, type, ...investmentInput};
      })
    };

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(Investment, data['updateInvestments'] as Investment[])),
        switchMap(investments => from(investments)),
        tap(investment => this.investmentUpdated.next(investment)),
        toArray()
      );
  }

  /**
   * Call the API to delete Investments.
   * @param investments Investments to delete.
   * @returns An Observable emitting true if all Investments have been deleted successfully, false otherwise.
   */
  public deleteInvestments(investments: Investment[]): Observable<boolean> {
    const mutation = gql`
      mutation DeleteInvestment($ids: [String!]!) {
        deleteInvestments(ids: $ids)
      }
    `;
    const variables = {ids: investments.map(investment => investment.id)};

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => data['deleteInvestments'] as boolean),
        tap(success => success && this.investmentsDeleted.next(investments))
      );
  }

  /**
   * Duplicate the Investments, copying all their data with a newly generated identifier.
   * @param investments Investments to duplicate.
   * @returns An Observable that emits each duplicated Investment.
   */
  public duplicateInvestments(investments: Investment[]): Observable<Investment> {
    const mutation = gql`
      mutation DuplicateInvestments($ids: [String!]!) {
        duplicateInvestments(ids: $ids) {
          ...InvestmentInfo
        }
      }
      ${this.investmentInfoGraphqlFragment}
    `;
    const variables = {ids: investments.map(investment => investment.id)};

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(Investment, data['duplicateInvestments'] as Investment[])),
        switchMap(duplicatedInvestments => from(duplicatedInvestments)),
        tap(duplicatedInvestment => this.investmentAdded.next(duplicatedInvestment))
      );
  }

  /**
   * Open the Investment side panel.
   * @param investment Investment to display in the side panel.
   */
  public openInvestmentSidePanel(investment: Investment): void {
    this.sidePanelToggle.next(investment);
  }

  /**
   * Close the Investment side panel.
   */
  public closeInvestmentSidePanel(): void {
    this.sidePanelToggle.next(null);
  }

  /**
   * Navigate to the Investment sheet.
   * @param id ID of the Investment to navigate to.
   */
  public async navigateToInvestmentSheet(id: string): Promise<void> {
    await this.router.navigate([
      'organization',
      this.appManager.currentOrganization.id,
      'investments',
      'investment-sheet',
      id
    ]);
  }
}
